import AccountPicker from "../../components/AccountPicker";
import TripPicker from "../../components/TripPicker";
import type { ReportingScope } from "../../types/reporting";
import { matchesCategory, reportingTransactions, transactionAllocations } from "../../lib/allocations";
import { ChevronDown, Search, SlidersHorizontal } from "lucide-react";
import type { Account, DashboardData, Transaction } from "../../types";
import { useMemo, type Dispatch, type SetStateAction } from "react";
import { isSpendable, money, total } from "../../lib/finance";
import { Private } from "../../components/Privacy";
import Empty from "../../components/Empty";
import LoadingLabel from "../../components/LoadingLabel";
import TransactionRow from "../../components/TransactionRow";
import TransactionActiveFilters from "./TransactionActiveFilters";

// The ledger itself: a filter panel, an optional bulk-categorise pass over anything still
// uncategorised, and the list. Props are grouped by the job they do — narrowing the list, running
// the categorise pass, or drawing it — because flat they are three dozen names in a row.

export interface TransactionsViewProps {
  data: DashboardData;
  today: string;
  selectedAccounts: Account[];
  list: {
    filtered: Transaction[];
    groups: string[];
    visibleCount: number;
    setVisibleCount: Dispatch<SetStateAction<number>>;
    trackedCounts: Map<string, number>;
  };
  filters: {
    reporting?: ReportingScope | null;
    setReporting?: (scope: ReportingScope | null) => void;
    open: boolean;
    setOpen: (open: boolean) => void;
    query: string;
    setQuery: (v: string) => void;
    category: string;
    setCategory: (v: string) => void;
    subcategory: string | null;
    setSubcategory: (v: string | null) => void;
    type: string;
    setType: (v: string) => void;
    accountFilter: string;
    setAccountFilter: (v: string) => void;
    dateFilter: string;
    setDateFilter: (v: string) => void;
    tripFilter?: string;
    setTripFilter?: (v: string) => void;
  };
  /** The uncategorised sweep: pick several rows, give them all one category. */
  review: {
    active: boolean;
    setActive: (v: boolean) => void;
    selection: string[];
    setSelection: Dispatch<SetStateAction<string[]>>;
    bulkCategory: string;
    setBulkCategory: (v: string) => void;
    busy: boolean;
    apply: () => void;
  };
  actions: {
    onOpenTransaction: (t: Transaction) => void;
    dateLabel: (date: string) => string;
  };
}

export default function TransactionsView(props: TransactionsViewProps) {
  const { data, today, selectedAccounts } = props;
  const { filtered, groups, visibleCount, setVisibleCount, trackedCounts } = props.list;
  const {
    open: filters, setOpen: setFilters, query, setQuery, category, setCategory,
    subcategory, type, setType, accountFilter, setAccountFilter,
    dateFilter, tripFilter, setTripFilter, reporting, setReporting,
  } = props.filters;
  const {
    active: reviewUncategorized, setActive: setReviewUncategorized,
    selection: categorySelection, setSelection: setCategorySelection,
    bulkCategory, setBulkCategory, busy: categorizing, apply: categorizeSelected,
  } = props.review;
  const { onOpenTransaction: setDetail, dateLabel } = props.actions;
  const selectedAccount = useMemo(
    () => (accountFilter !== "All" ? (data.accounts || []).find((a) => a.id === accountFilter) : undefined),
    [accountFilter, data.accounts],
  );
  const needsReviewCount = useMemo(
    () => (data.transactions || []).filter((t) => t.needsReview).length,
    [data.transactions],
  );
  // Each day heading shows that day's spend and rows. One pass over the list builds both, instead of
  // every heading filtering the whole list again.
  const days = useMemo(() => {
    const spend = new Map<string, Transaction[]>(), visible = new Map<string, Transaction[]>();
    const add = (map: Map<string, Transaction[]>, t: Transaction) => {
      const day = t.date.slice(0, 10);
      (map.get(day) ?? map.set(day, []).get(day)!).push(t);
    };
    for (const t of reportingTransactions(filtered)) if (isSpendable(t) && matchesCategory(t, category, subcategory)) add(spend, t);
    for (const t of filtered.slice(0, visibleCount)) add(visible, t);
    return {
      spent: (day: string) => {
        const items = spend.get(day) ?? [];
        if (selectedAccount) {
          return items.reduce((s, t) => s + (t.reportableAmount ?? t.amount), 0);
        }
        return total(items);
      },
      rows: (day: string) => visible.get(day) ?? [],
    };
  }, [filtered, visibleCount, category, subcategory, selectedAccount]);

  const activeCount = [accountFilter, category, type, dateFilter, tripFilter].filter(v => v && v !== "All").length
    + Number(subcategory !== null) + Number(!!reporting) + Number(reporting?.merchant !== undefined)
    + Number(!!reporting && reporting.activity !== "all");
  return (
    <section className="panel transactions-panel">
      {reviewUncategorized && <div className="categorize-tools active">
        <button disabled={categorizing} onClick={() => { setReviewUncategorized(false); setCategory("All"); setType("All"); setQuery(""); setAccountFilter("All"); setCategorySelection([]); }}>Back to monthly history</button>
        {reviewUncategorized && <><p>Uncategorized expenses · all dates · {categorySelection.length} selected</p><label>Assign category<select disabled={categorizing} value={bulkCategory} onChange={e => setBulkCategory(e.target.value)}><option value="">Choose category</option>{(data.entryOptions?.categories || []).filter(c => c !== "Uncategorized").map(c => <option key={c}>{c}</option>)}</select></label><button disabled={categorizing || !bulkCategory || !categorySelection.length} onClick={() => void categorizeSelected()}>{categorizing ? <LoadingLabel>Saving</LoadingLabel> : `Apply to ${categorySelection.length} selected`}</button></>}
      </div>}
      {needsReviewCount > 0 && !reviewUncategorized && (
        <div className="active-filters" role="status">
          <span>
            {needsReviewCount} {needsReviewCount === 1 ? "transaction" : "transactions"} awaiting review
          </span>
          <button type="button" onClick={() => setQuery("SMS Ref")}>
            Review items
          </button>
        </div>
      )}
      <div className="history-toolbar">
        <div className="search-field">
          <Search size={19} />
          <input
            type="search"
            aria-label="Search transactions"
            placeholder="Search a shop, category, or note…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <button
          className={
            "filter-button " + (filters ? "selected" : "")
          }
          aria-label={filters ? "Hide transaction filters" : "Show transaction filters"}
          aria-expanded={filters}
          onClick={() => setFilters(!filters)}
        >
          <SlidersHorizontal size={18} />
          <span>Filters</span>
          {activeCount > 0 && <i>{activeCount}</i>}
        </button>
      </div>
      {filters && (
        <div className="filter-fields">
          <AccountPicker label="Account" accounts={selectedAccounts} value={accountFilter} onChange={(id) => setAccountFilter(id)} emptyOption={{ value: "All", label: "All accounts" }} />
          <label>
            Category
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="All">All categories</option>
              {Array.from(
                new Set((data.transactions || []).flatMap(t => transactionAllocations(t).map(p => p.category))),
              )
                .sort()
                .map((c) => (
                  <option key={c}>{c}</option>
                ))}
            </select>
          </label>
          <label>
            Type
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option>All</option>
              <option>Expense</option>
              <option>Income</option>
              <option>Transfer</option>
              <option>Pending</option>
            </select>
          </label>
          {(data.trips || []).length > 0 && tripFilter !== undefined && setTripFilter && (
            <TripPicker
              label="Trip"
              trips={data.trips || []}
              value={tripFilter}
              emptyOption={{ value: "All", label: "All trips & everyday" }}
              onChange={(tid) => setTripFilter(tid || "All")}
            />
          )}
        </div>
      )}
      <TransactionActiveFilters data={data} filters={props.filters} today={today} dateLabel={dateLabel} />
      <div className="results-caption">
        <span>
          {filtered.length} transaction
          {filtered.length !== 1 ? "s" : ""}
        </span>
        {reviewUncategorized ? <span>Uncategorized</span> : <button type="button" onClick={() => { setReporting?.(null); setReviewUncategorized(true); setCategory("All"); setType("All"); setQuery(""); setAccountFilter("All"); setCategorySelection([]); }}>Categorize</button>}
      </div>
      {filtered.length ? (
        groups.map((day) => (
          <div className="transaction-day" key={day}>
            <div className="day-heading">
              <span>
                {day === today ? "Today" : dateLabel(day)}
              </span>
              <span>
                <Private>{money(days.spent(day), selectedAccount?.currency)}</Private>{" "}
                {category !== "All" || subcategory !== null ? "in category" : "spent"}
              </span>
            </div>
            {days.rows(day)
              .map((t) => (
                <div key={t.id}>
                {reviewUncategorized && <label><input type="checkbox" disabled={categorizing} checked={categorySelection.includes(t.id)} onChange={e => setCategorySelection(ids => e.target.checked ? [...ids, t.id] : ids.filter(id => id !== t.id))} />Select {t.title || t.description || t.id}</label>}
                <TransactionRow
                  key={t.id}
                  t={t}
                  accounts={data.accounts || []}
                  payees={data.payees}
                  trips={data.trips}
                  trackedCount={trackedCounts.get(t.id) || 0}
                  amountBasis="reportable"
                  perspectiveAccountId={selectedAccount?.id}
                  onClick={() => setDetail(t)}
                />
                </div>
              ))}
          </div>
        ))
      ) : (
        <Empty
          title="No matching transactions"
          description="Try another month, profile, or search."
        />
      )}
      {filtered.length > visibleCount && (
        <button
          className="load-more"
          onClick={() => setVisibleCount((v) => v + 40)}
        >
          Show more transactions <ChevronDown size={16} />
        </button>
      )}
    </section>
  );
}
